/**
 * Mail Hub 收件箱详情页
 *
 * 单个收件箱的完整控制面，按语义分区：
 *   1. 基础配置区 — 它是谁，归谁管，接到哪里
 *   2. 采集配置区 — 系统希望它如何采集
 *   3. 运行控制区 — 现在要不要让它跑
 *   4. 近期活动区 — 最近几轮到底发生了什么
 *   5. 任务摘要区 — 它采进来的内容有没有堵住后续处理链
 *
 * → docs/20_inbound_mail_processing/06_mailbox_control_plane_and_observability.md §5
 */

import React, { useState, useEffect, useCallback } from "react";
import {
    Card,
    Descriptions,
    Button,
    Space,
    Tag,
    App,
    Spin,
    Typography,
    Divider,
    Row,
    Col,
    Statistic,
    Form,
    InputNumber,
    Switch,
    Input,
    Result,
    Modal,
    Breadcrumb,
    Alert,
    Select,
    Tabs,
    Popconfirm,
} from "antd";
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    EditOutlined,
    ExclamationCircleOutlined,
    LinkOutlined,
    MailOutlined,
    PauseCircleOutlined,
    PlayCircleOutlined,
    ReloadOutlined,
    SaveOutlined,
    SyncOutlined,
    ThunderboltOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams, Link } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/zh-cn";

import {
    listMailboxes,
    getMailbox,
    updateMailbox,
    testMailboxConnection,
    triggerMailboxSync,
    getMailboxIngestSummary,
    getMailboxPendingSummary,
    getMailboxBusinessJudgmentAvailability,
    listMailboxPermissions,
    createOrgSendAsGrant,
    revokePermission,
} from "../../../api/v3/mailHubApi";
import {
    formatMailHubError,
    readMailHubData,
    validateBjaAvailabilityPayload,
    validateIngestionSummaryPayload,
    validateMailboxDetailPayload,
    validateMailboxListPayload,
    validateMailboxIngestSummaryPayload,
    validateMailboxPendingSummaryPayload,
    validateMailboxPermissionsPayload,
    validateTestConnectionPayload,
} from "../lib/contracts";

dayjs.extend(relativeTime);
dayjs.locale("zh-cn");

const { Title, Text, Paragraph } = Typography;

const PROCESSING_MODE_OPTIONS = [
    { value: "observe_only", label: "观察模式", color: "blue", description: "只收信、存储、展示和分类观察，强制关闭业务副作用。" },
    { value: "controlled", label: "受控处理", color: "gold", description: "仅允许显式打开的能力执行。" },
    { value: "full_processing", label: "完整处理", color: "red", description: "仅放开后端生产准入白名单内能力；当前只有 OCR 外部调用会自动生效。" },
];

const PROCESSING_CAPABILITIES = [
    { key: "mvt_parse", label: "MVT 解析", supported: true },
    { key: "cpm_parse", label: "CPM 解析", supported: true },
    { key: "ffm_parse", label: "FFM 解析", supported: true },
    { key: "fwb_parse", label: "FWB 解析", supported: true },
    { key: "cgo_attachment_parse", label: "CGO 附件解析", supported: true },
    { key: "ocr_external_call", label: "OCR 外部调用", supported: true, fullProcessingEnabled: true },
    { key: "business_judgment", label: "BJA 研判", supported: true },
    { key: "ucm_forward", label: "UCM 转发入库", supported: true },
    { key: "fuel_forward", label: "燃油报文转发入库", supported: true },
    { key: "hkg_cargo_attachment_forward", label: "香港货运附件转发入库", supported: true },
    { key: "auto_forward", label: "自动转发", supported: false },
    { key: "auto_send", label: "自动发送", supported: true },
];

const PIPELINE_STATUS_META = {
    active: { label: "active", color: "green" },
    draining: { label: "draining", color: "gold" },
    paused: { label: "paused", color: "orange" },
    disabled: { label: "disabled", color: "red" },
};

const BJA_BLOCKING_LAYER_LABELS = {
    pipeline_item_type: "全局任务类型 Gate",
    pipeline_transition: "自动触发链 Gate",
    mailbox_processing_mode: "邮箱处理模式",
    mailbox_capability: "邮箱 BJA 能力开关",
};

function modeOption(mode) {
    return PROCESSING_MODE_OPTIONS.find((item) => item.value === mode) || null;
}

function buildCapabilityFormValues(mailbox) {
    const mode = mailbox.processing_mode;
    const configured = mailbox.processing_capabilities;
    return PROCESSING_CAPABILITIES.reduce((acc, capability) => {
        if (typeof configured[capability.key] === "boolean") {
            acc[capability.key] = configured[capability.key];
        } else {
            acc[capability.key] = mode === "full_processing" && capability.fullProcessingEnabled === true;
        }
        return acc;
    }, {});
}

function effectiveCapability(mailbox, capabilityKey) {
    return mailbox.effective_processing_policy.effective_capabilities[capabilityKey] === true;
}

function renderPipelineStatusTag(status) {
    const meta = PIPELINE_STATUS_META[status];
    if (!meta) {
        return <Tag color="error">未知策略状态：{status}</Tag>;
    }
    return <Tag color={meta.color}>{meta.label}</Tag>;
}

function formatPolicyOverride(override) {
    if (!override) {
        return "默认策略";
    }
    const time = dayjs(override.updated_at).format("YYYY-MM-DD HH:mm:ss");
    return `${override.updated_by} · ${time}${override.reason ? ` · ${override.reason}` : ""}`;
}

function manualBjaBlockReason(availability) {
    if (!availability || availability.manual_trigger_available) {
        return null;
    }
    if (!availability.item_type_gate?.allowed) {
        return availability.item_type_gate.reason || "全局 business_judgment 任务类型当前不允许创建";
    }
    if (!availability.mailbox_gate?.allowed) {
        return availability.mailbox_gate.reason || "当前邮箱处理策略未允许 BJA 研判";
    }
    return "当前 BJA 手动触发不可用";
}

const BusinessJudgmentAvailabilityPreview = ({ availability, error }) => {
    if (error) {
        return <Alert type="error" showIcon message="BJA 多层 Gate 状态不可用" description={error} />;
    }

    if (!availability) {
        return <Text type="secondary">BJA 多层 Gate 状态加载中…</Text>;
    }

    const manualReason = manualBjaBlockReason(availability);
    const pipelineReason = availability.available
        ? null
        : availability.blocking_layer
            ? BJA_BLOCKING_LAYER_LABELS[availability.blocking_layer]
            : "存在未通过的 Gate";

    return (
        <Space direction="vertical" size={6} style={{ width: "100%" }}>
            <Space wrap>
                <Tag color={availability.manual_trigger_available ? "green" : "red"}>
                    手动触发：{availability.manual_trigger_available ? "可用" : "不可用"}
                </Tag>
                <Tag color={availability.available ? "green" : "orange"}>
                    自动链：{availability.available ? "可用" : "受阻"}
                </Tag>
                {pipelineReason ? <Tag color="orange">阻断层：{pipelineReason}</Tag> : null}
            </Space>
            <Space wrap>
                <Text type="secondary">任务类型</Text>
                {renderPipelineStatusTag(availability.item_type_gate.status)}
                <Text type="secondary">触发链 {availability.transition_id}</Text>
                {renderPipelineStatusTag(availability.transition_gate.status)}
                <Text type="secondary">邮箱 BJA</Text>
                <Tag color={availability.mailbox_gate.allowed ? "green" : "red"}>
                    {availability.mailbox_gate.allowed ? "允许" : "禁止"}
                </Tag>
            </Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
                任务类型策略：{formatPolicyOverride(availability.item_type_gate.policy_override)}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
                触发链策略：{formatPolicyOverride(availability.transition_gate.policy_override)}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
                邮箱策略：{availability.mailbox_gate.updated_by || "未记录修改人"} · {availability.mailbox_gate.updated_at ? dayjs(availability.mailbox_gate.updated_at).format("YYYY-MM-DD HH:mm:ss") : "未记录更新时间"}
            </Text>
            {manualReason ? <Text type="danger" style={{ fontSize: 12 }}>{manualReason}</Text> : null}
        </Space>
    );
};

function blankToNull(value) {
    if (typeof value !== "string") {
        return value ?? null;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
}

// ---------------------------------------------------------------------------
// 子组件 — 基础配置区
// ---------------------------------------------------------------------------

const BasicConnectionSection = ({ mailbox, platformDefaultMailbox, onSave, saving }) => {
    const [editing, setEditing] = useState(false);
    const [form] = Form.useForm();
    const isSystemMailbox = mailbox.mailbox_type === "system_mailbox";
    const switchColSpan = isSystemMailbox ? 8 : 12;

    useEffect(() => {
        form.setFieldsValue({
            display_name: mailbox.display_name,
            provider: mailbox.provider,
            imap_host: mailbox.imap_host,
            imap_port: mailbox.imap_port,
            smtp_host: mailbox.smtp_host,
            smtp_port: mailbox.smtp_port,
            is_receive_enabled: mailbox.is_receive_enabled,
            is_send_enabled: mailbox.is_send_enabled,
            is_platform_default: mailbox.is_platform_default,
            credential_user: undefined,
            credential_pass: undefined,
        });
    }, [mailbox, form]);

    const confirmPlatformDefaultChange = (values) => new Promise((resolve) => {
        const willEnablePlatformDefault = values.is_platform_default === true && mailbox.is_platform_default !== true;
        const hasOtherPlatformDefault = Boolean(platformDefaultMailbox && platformDefaultMailbox.id !== mailbox.id);
        if (!isSystemMailbox || !willEnablePlatformDefault || !hasOtherPlatformDefault) {
            resolve(true);
            return;
        }

        Modal.confirm({
            title: "将替换当前的平台默认邮箱",
            content: (
                <div>
                    <Paragraph style={{ marginBottom: 8 }}>
                        当前平台默认邮箱为 {platformDefaultMailbox.email_address}。确认后，系统邮件将改用此邮箱发送。
                    </Paragraph>
                    <Text type="secondary">全局只能有一个平台默认邮箱。</Text>
                </div>
            ),
            okText: "确认替换",
            cancelText: "取消",
            okButtonProps: { danger: true },
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
        });
    });

    const handleSave = async () => {
        const values = await form.validateFields();
        if (!await confirmPlatformDefaultChange(values)) return;
        const credentialUser = values.credential_user?.trim();
        const credentialPass = values.credential_pass;
        const shouldUpdateCredential = Boolean(credentialUser || credentialPass);

        if (shouldUpdateCredential) {
            if (!credentialPass) {
                form.setFields([{ name: "credential_pass", errors: ["更新凭据时请输入新密码或客户端授权码"] }]);
                return;
            }
        }

        const payload = {
            display_name: blankToNull(values.display_name),
            provider: blankToNull(values.provider),
            imap_host: blankToNull(values.imap_host),
            imap_port: values.imap_port ?? null,
            smtp_host: blankToNull(values.smtp_host),
            smtp_port: values.smtp_port ?? null,
            is_receive_enabled: values.is_receive_enabled ?? false,
            is_send_enabled: values.is_send_enabled ?? false,
        };

        if (isSystemMailbox) {
            payload.is_platform_default = values.is_platform_default ?? false;
        }

        if (shouldUpdateCredential) {
            payload.credential = {
                user: credentialUser || mailbox.email_address,
                pass: credentialPass,
            };
        }

        await onSave(payload);
        form.setFieldValue("credential_user", undefined);
        form.setFieldValue("credential_pass", undefined);
        setEditing(false);
    };

    if (!editing) {
        return (
            <div>
                <Descriptions column={2} bordered size="small">
                    <Descriptions.Item label="邮箱地址">{mailbox.email_address}</Descriptions.Item>
                    <Descriptions.Item label="显示名">{mailbox.display_name || "—"}</Descriptions.Item>
                    <Descriptions.Item label="邮箱类型">
                        <Tag>{mailbox.mailbox_type === "system_mailbox" ? "系统邮箱" : "个人邮箱"}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="提供方">{mailbox.provider || "—"}</Descriptions.Item>
                    <Descriptions.Item label="IMAP">
                        {mailbox.imap_host ? `${mailbox.imap_host}:${mailbox.imap_port}` : "—"}
                    </Descriptions.Item>
                    <Descriptions.Item label="SMTP">
                        {mailbox.smtp_host ? `${mailbox.smtp_host}:${mailbox.smtp_port}` : "—"}
                    </Descriptions.Item>
                    <Descriptions.Item label="收信">
                        {mailbox.is_receive_enabled ? <Tag color="green">已启用</Tag> : <Tag color="default">已关闭</Tag>}
                    </Descriptions.Item>
                    <Descriptions.Item label="发信">
                        {mailbox.is_send_enabled ? <Tag color="green">已启用</Tag> : <Tag color="default">已关闭</Tag>}
                    </Descriptions.Item>
                    {isSystemMailbox && (
                        <Descriptions.Item label="平台默认">
                            {mailbox.is_platform_default ? <Tag color="green">已设为默认</Tag> : <Tag color="default">未设置</Tag>}
                        </Descriptions.Item>
                    )}
                    <Descriptions.Item label="凭据">
                        {mailbox.has_credential ? (
                            <Tag color="green" icon={<CheckCircleOutlined />}>已配置</Tag>
                        ) : (
                            <Tag color="red" icon={<CloseCircleOutlined />}>未配置</Tag>
                        )}
                    </Descriptions.Item>
                    <Descriptions.Item label="资源状态">
                        {mailbox.is_active ? (
                            <Tag color="green">活跃</Tag>
                        ) : (
                            <Tag color="red">已停用</Tag>
                        )}
                    </Descriptions.Item>
                </Descriptions>
                <div style={{ marginTop: 12, textAlign: "right" }}>
                    <Button icon={<EditOutlined />} onClick={() => setEditing(true)}>
                        编辑基础与连接配置
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <Form form={form} layout="vertical">
            <Alert
                showIcon
                type="info"
                style={{ marginBottom: 12 }}
                message="凭据字段留空会保留当前凭据"
                description="更新密码或客户端授权码时，只填新密码会默认使用邮箱地址作为 IMAP 用户名；用户名不同再单独填写。网易企业邮箱通常需要填写客户端授权码，而不是网页登录密码。邮箱地址和所属组织不能在这里变更。"
            />
            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item name="display_name" label="显示名">
                        <Input placeholder="邮箱显示名称" />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item name="provider" label="提供方">
                        <Input placeholder="如 163 企业邮箱、Gmail、QQ" />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item name="imap_host" label="IMAP 主机">
                        <Input placeholder="imap.example.com" />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item name="imap_port" label="IMAP 端口">
                        <InputNumber min={1} max={65535} placeholder="993" style={{ width: "100%" }} />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item name="smtp_host" label="SMTP 主机">
                        <Input placeholder="smtp.example.com" />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item name="smtp_port" label="SMTP 端口">
                        <InputNumber min={1} max={65535} placeholder="465" style={{ width: "100%" }} />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item name="credential_user" label="IMAP 用户名">
                        <Input placeholder="通常为邮箱地址" />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item name="credential_pass" label="新密码或客户端授权码">
                        <Input.Password placeholder="留空则不更新凭据" />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={switchColSpan}>
                    <Form.Item name="is_receive_enabled" label="启用收信" valuePropName="checked">
                        <Switch checkedChildren="开启" unCheckedChildren="关闭" />
                    </Form.Item>
                </Col>
                <Col span={switchColSpan}>
                    <Form.Item name="is_send_enabled" label="启用发信" valuePropName="checked">
                        <Switch checkedChildren="开启" unCheckedChildren="关闭" />
                    </Form.Item>
                </Col>
                {isSystemMailbox && (
                    <Col span={8}>
                        <Form.Item
                            name="is_platform_default"
                            label="平台默认"
                            valuePropName="checked"
                            extra="设为平台默认后，系统邮件（如密码重置）将使用此邮箱发送。全局只能有一个平台默认邮箱。"
                        >
                            <Switch checkedChildren="默认" unCheckedChildren="普通" />
                        </Form.Item>
                    </Col>
                )}
            </Row>
            <Space>
                <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
                    保存基础与连接配置
                </Button>
                <Button onClick={() => setEditing(false)}>取消</Button>
            </Space>
        </Form>
    );
};

// ---------------------------------------------------------------------------
// 子组件 — 采集配置编辑区
// ---------------------------------------------------------------------------

const SchedulingConfigSection = ({ mailbox, onSave, saving }) => {
    const [editing, setEditing] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        form.setFieldsValue({
            poll_interval_seconds: mailbox.poll_interval_seconds,
            fetch_since_days: mailbox.fetch_since_days,
            max_fetch_per_round: mailbox.max_fetch_per_round,
            store_attachments_in_oss: mailbox.store_attachments_in_oss,
            store_inline_images_in_oss: mailbox.store_inline_images_in_oss,
        });
    }, [mailbox, form]);

    const handleSave = async () => {
        const values = await form.validateFields();
        await onSave(values);
        setEditing(false);
    };

    if (!editing) {
        return (
            <div>
                <Descriptions column={2} bordered size="small">
                    <Descriptions.Item label="轮询间隔">{mailbox.poll_interval_seconds} 秒</Descriptions.Item>
                    <Descriptions.Item label="首次抓取窗口">{mailbox.fetch_since_days} 天</Descriptions.Item>
                    <Descriptions.Item label="单轮上限">{mailbox.max_fetch_per_round} 封</Descriptions.Item>
                    <Descriptions.Item label="自动轮询">
                        {mailbox.is_poll_enabled ? (
                            <Tag color="green">已启用</Tag>
                        ) : (
                            <Tag color="orange">已暂停</Tag>
                        )}
                    </Descriptions.Item>
                    <Descriptions.Item label="附件落 OSS">
                        {mailbox.store_attachments_in_oss ? (
                            <Tag color="green">已开启</Tag>
                        ) : (
                            <Tag color="default">已关闭</Tag>
                        )}
                    </Descriptions.Item>
                    <Descriptions.Item label="内嵌图片落 OSS">
                        {mailbox.store_inline_images_in_oss ? (
                            <Tag color="green">已开启</Tag>
                        ) : (
                            <Tag color="default">已关闭</Tag>
                        )}
                    </Descriptions.Item>
                </Descriptions>
                <div style={{ marginTop: 12, textAlign: "right" }}>
                    <Button icon={<EditOutlined />} onClick={() => setEditing(true)}>
                        编辑配置
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <Form form={form} layout="vertical">
            <Row gutter={16}>
                <Col span={8}>
                    <Form.Item
                        name="poll_interval_seconds"
                        label="轮询间隔 (秒)"
                        rules={[{ required: true }, { type: "number", min: 10, message: "不能小于 10 秒" }]}
                    >
                        <InputNumber min={10} max={86400} style={{ width: "100%" }} />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item
                        name="fetch_since_days"
                        label="首次抓取窗口 (天)"
                        rules={[{ required: true }, { type: "number", min: 1 }]}
                    >
                        <InputNumber min={1} max={365} style={{ width: "100%" }} />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item
                        name="max_fetch_per_round"
                        label="单轮抓取上限 (封)"
                        rules={[{ required: true }, { type: "number", min: 1 }]}
                    >
                        <InputNumber min={1} max={1000} style={{ width: "100%" }} />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item name="store_attachments_in_oss" label="附件落 OSS" valuePropName="checked">
                        <Switch checkedChildren="开启" unCheckedChildren="关闭" />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item name="store_inline_images_in_oss" label="内嵌图片落 OSS" valuePropName="checked">
                        <Switch checkedChildren="开启" unCheckedChildren="关闭" />
                    </Form.Item>
                </Col>
            </Row>
            <Space>
                <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
                    保存
                </Button>
                <Button onClick={() => setEditing(false)}>取消</Button>
            </Space>
        </Form>
    );
};

// ---------------------------------------------------------------------------
// 子组件 — 处理模式与副作用 Gate
// ---------------------------------------------------------------------------

const ProcessingPolicySection = ({ mailbox, bjaAvailability, bjaAvailabilityError, onSave, saving }) => {
    const [editing, setEditing] = useState(false);
    const [form] = Form.useForm();
    const mode = modeOption(mailbox.processing_mode);
    const watchedMode = Form.useWatch("processing_mode", form);

    useEffect(() => {
        form.setFieldsValue({
            processing_mode: mailbox.processing_mode,
            processing_capabilities: buildCapabilityFormValues(mailbox),
        });
    }, [mailbox, form]);

    const handleModeChange = (nextMode) => {
        if (nextMode === "full_processing") {
            form.setFieldValue("processing_capabilities", PROCESSING_CAPABILITIES.reduce((acc, capability) => {
                acc[capability.key] = capability.fullProcessingEnabled === true;
                return acc;
            }, {}));
        }
    };

    const confirmRiskyChange = (values) => new Promise((resolve) => {
        const nextCapabilities = values.processing_capabilities || {};
        const currentCapabilities = mailbox.processing_capabilities;
        const modeEscalated = mailbox.processing_mode === "observe_only" && values.processing_mode !== "observe_only";
        const enabledCapabilities = PROCESSING_CAPABILITIES
            .filter((capability) => nextCapabilities[capability.key] === true && currentCapabilities[capability.key] !== true)
            .map((capability) => capability.label);

        if (!modeEscalated && enabledCapabilities.length === 0) {
            resolve(true);
            return;
        }

        Modal.confirm({
            title: "确认提高处理风险级别？",
            content: (
                <div>
                    <Paragraph style={{ marginBottom: 8 }}>
                        该变更可能允许 Mail Hub 执行业务写入、BJA 研判或外部邮件副作用。请确认旧邮件服务中同类能力已停用，且该邮箱已完成测试验证。
                    </Paragraph>
                    {modeEscalated && <Tag color="gold">从观察模式切换为 {modeOption(values.processing_mode).label}</Tag>}
                    {enabledCapabilities.length > 0 && (
                        <div style={{ marginTop: 8 }}>
                            <Text type="secondary">将打开能力：</Text>
                            <Space wrap style={{ marginLeft: 8 }}>
                                {enabledCapabilities.map((label) => <Tag color="red" key={label}>{label}</Tag>)}
                            </Space>
                        </div>
                    )}
                </div>
            ),
            okText: "确认保存",
            cancelText: "取消",
            okButtonProps: { danger: true },
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
        });
    });

    const handleSave = async () => {
        const values = await form.validateFields();
        if (!await confirmRiskyChange(values)) return;
        await onSave({
            processing_mode: values.processing_mode,
            processing_capabilities: values.processing_capabilities || {},
        });
        setEditing(false);
    };

    if (!editing) {
        return (
            <div>
                <Descriptions column={2} bordered size="small">
                    <Descriptions.Item label="处理模式">
                        {mode ? (
                            <>
                                <Tag color={mode.color}>{mode.label}</Tag>
                                <Text type="secondary" style={{ marginLeft: 8 }}>{mode.description}</Text>
                            </>
                        ) : <Tag color="error">未知处理模式：{mailbox.processing_mode}</Tag>}
                    </Descriptions.Item>
                    <Descriptions.Item label="策略更新时间">
                        {mailbox.processing_policy_updated_at
                            ? dayjs(mailbox.processing_policy_updated_at).format("YYYY-MM-DD HH:mm:ss")
                            : "—"}
                    </Descriptions.Item>
                    <Descriptions.Item label="策略修改人">
                        {mailbox.processing_policy_updated_by || "—"}
                    </Descriptions.Item>
                    <Descriptions.Item label="能力生效预览" span={2}>
                        <Space wrap>
                            {PROCESSING_CAPABILITIES.map((capability) => {
                                const enabled = effectiveCapability(mailbox, capability.key);
                                return (
                                    <Tag key={capability.key} color={enabled ? "green" : "default"}>
                                        {capability.label}: {enabled ? "允许" : "禁止"}
                                    </Tag>
                                );
                            })}
                        </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="BJA 合成可用性" span={2}>
                        <BusinessJudgmentAvailabilityPreview availability={bjaAvailability} error={bjaAvailabilityError} />
                    </Descriptions.Item>
                </Descriptions>
                <div style={{ marginTop: 12, textAlign: "right" }}>
                    <Button icon={<EditOutlined />} onClick={() => setEditing(true)}>
                        编辑处理策略
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <Form form={form} layout="vertical">
            <Alert
                showIcon
                type="warning"
                style={{ marginBottom: 12 }}
                message="策略会即时影响后续处理"
                description="观察模式会强制关闭全部业务副作用；受控处理只执行显式打开的能力；完整处理当前仅放开 OCR 外部调用，其他业务副作用仍受生产准入白名单阻断。关闭开关后，尚未创建、尚未执行或尚未写入的副作用会被 gate 阻止。"
            />
            <Form.Item name="processing_mode" label="处理模式" rules={[{ required: true }]}>
                <Select
                    onChange={handleModeChange}
                    options={PROCESSING_MODE_OPTIONS.map(({ value, label, description }) => ({
                        value,
                        label: `${label} — ${description}`,
                    }))}
                />
            </Form.Item>
            <Row gutter={16}>
                {PROCESSING_CAPABILITIES.map((capability) => (
                    <Col span={8} key={capability.key}>
                        <Form.Item
                            name={["processing_capabilities", capability.key]}
                            label={capability.label}
                            valuePropName="checked"
                        >
                            <Switch
                                checkedChildren="允许"
                                unCheckedChildren={capability.supported ? "禁止" : "未实现"}
                                disabled={!capability.supported || watchedMode === "full_processing"}
                            />
                        </Form.Item>
                    </Col>
                ))}
            </Row>
            <Space>
                <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
                    保存处理策略
                </Button>
                <Button onClick={() => setEditing(false)}>取消</Button>
            </Space>
        </Form>
    );
};

// ---------------------------------------------------------------------------
// 子组件 — 近期活动区
// ---------------------------------------------------------------------------

const RecentActivitySection = ({ mailbox, ingestSummary, ingestSummaryError }) => {
    if (ingestSummaryError) {
        return <Alert type="error" showIcon message="近期活动统计不可用" description={ingestSummaryError} />;
    }

    if (!ingestSummary) {
        return <Text type="secondary">近期活动统计加载中…</Text>;
    }

    return (
    <div>
        <Row gutter={16}>
            <Col span={6}>
                <Statistic
                    title="最近同步"
                    value={
                        mailbox.last_sync_at
                            ? dayjs(mailbox.last_sync_at).format("MM-DD HH:mm")
                            : "从未"
                    }
                />
            </Col>
            <Col span={6}>
                <Statistic
                    title="24h 新增 Receipt"
                    value={ingestSummary.recentReceiptCount}
                    valueStyle={
                        ingestSummary.recentReceiptCount > 0
                            ? { color: "#1677ff" }
                            : undefined
                    }
                />
            </Col>
            <Col span={6}>
                <Statistic
                    title="24h 处理事件"
                    value={ingestSummary.recentProcessingEventCount}
                />
            </Col>
            <Col span={6}>
                <Statistic
                    title="24h 失败数"
                    value={ingestSummary.recentFailedCount}
                    valueStyle={
                        ingestSummary.recentFailedCount > 0
                            ? { color: "#ff4d4f" }
                            : undefined
                    }
                />
            </Col>
        </Row>
        {mailbox.last_poll_error && (
            <div style={{ marginTop: 12 }}>
                <Text type="danger">
                    <ExclamationCircleOutlined style={{ marginRight: 4 }} />
                    最近错误：
                </Text>
                <Paragraph
                    type="danger"
                    ellipsis={{ rows: 2, expandable: true }}
                    style={{ marginTop: 4, marginBottom: 0 }}
                >
                    {mailbox.last_poll_error}
                </Paragraph>
            </div>
        )}
    </div>
    );
};

// ---------------------------------------------------------------------------
// 子组件 — 处理积压摘要区
// ---------------------------------------------------------------------------

const PendingSummarySection = ({ pendingSummary, pendingSummaryError }) => {
    if (pendingSummaryError) {
        return <Alert type="error" showIcon message="处理积压摘要不可用" description={pendingSummaryError} />;
    }

    if (!pendingSummary) {
        return <Text type="secondary">加载中…</Text>;
    }

    if (pendingSummary.total === 0) {
        return (
            <Result
                status="success"
                title="无积压"
                subTitle="当前邮箱的所有处理事件均已推进到终态"
                style={{ padding: "16px 0" }}
            />
        );
    }

    const statusLabels = {
        new: "待标准化",
        normalized: "待分类",
        classified: "待分发",
        failed: "失败待重试",
        retried: "重试中",
    };

    return (
        <Row gutter={16}>
            {pendingSummary.byStatus.map((item) => (
                <Col span={6} key={item.status}>
                    <Statistic
                        title={statusLabels[item.status] || item.status}
                        value={item.count}
                        valueStyle={
                            item.status === "failed" ? { color: "#ff4d4f" } : undefined
                        }
                    />
                </Col>
            ))}
            <Col span={6}>
                <Statistic
                    title="总积压"
                    value={pendingSummary.total}
                    valueStyle={pendingSummary.total > 0 ? { color: "#faad14" } : undefined}
                />
            </Col>
        </Row>
    );
};

// ---------------------------------------------------------------------------
// 子组件 — 发送授权区
// ---------------------------------------------------------------------------

/**
 * 判断邮箱授权列表中是否存在组织级 send_as 授权。
 *
 * org-scope grant 的特征：mailbox_id 为 null、action 为 send_as、is_active 为 true。
 */
function findOrgSendAsGrant(grants) {
    if (!Array.isArray(grants)) {
        return null;
    }
    return grants.find(
        (grant) => grant.mailbox_id === null
            && grant.action === "send_as"
            && grant.is_active === true,
    ) || null;
}

const SendAsPermissionSection = ({ mailboxId, mailboxType }) => {
    const { message } = App.useApp();
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [grants, setGrants] = useState(null);
    const [error, setError] = useState(null);

    const loadPermissions = useCallback(async () => {
        setLoading(true);
        try {
            const res = await listMailboxPermissions(mailboxId);
            const data = validateMailboxPermissionsPayload(readMailHubData(res, "邮箱授权"));
            setGrants(data.grants);
            setError(null);
        } catch (err) {
            setError(formatMailHubError(err, "授权状态加载失败"));
            setGrants(null);
            message.error(formatMailHubError(err, "授权状态加载失败"));
        } finally {
            setLoading(false);
        }
    }, [mailboxId, message]);

    useEffect(() => {
        loadPermissions();
    }, [loadPermissions]);

    const orgGrant = findOrgSendAsGrant(grants);
    const isEnabled = orgGrant !== null;
    const isSystemMailbox = mailboxType === "system_mailbox";

    const handleEnable = async () => {
        setActionLoading(true);
        try {
            await createOrgSendAsGrant();
            message.success("已开启组织级发送授权");
            await loadPermissions();
        } catch (err) {
            message.error(formatMailHubError(err, "开启发送授权失败"));
        } finally {
            setActionLoading(false);
        }
    };

    const handleDisable = async () => {
        if (!orgGrant) return;
        setActionLoading(true);
        try {
            await revokePermission(orgGrant.id);
            message.success("已关闭组织级发送授权");
            await loadPermissions();
        } catch (err) {
            message.error(formatMailHubError(err, "关闭发送授权失败"));
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) {
        return <Text type="secondary">授权状态加载中…</Text>;
    }

    if (error) {
        return <Alert type="error" showIcon message="授权状态不可用" description={error} />;
    }

    return (
        <div>
            <Alert
                showIcon
                type="info"
                style={{ marginBottom: 16 }}
                message="组织级发送授权"
                description="开启后，组织内所有成员都可以使用此邮箱作为发件人发送邮件。此授权仅对 system_mailbox 生效；user_mailbox 不受组织级授权影响，需逐用户配置邮箱级授权。"
            />
            <Card size="small">
                <Space style={{ width: "100%", justifyContent: "space-between" }}>
                    <Space>
                        <Tag color={isEnabled ? "green" : "default"} icon={isEnabled ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
                            {isEnabled ? "组织级发送授权已开启" : "组织级发送授权未开启"}
                        </Tag>
                        {!isEnabled && (
                            <Text type="secondary">当前组织成员无法使用此邮箱发信</Text>
                        )}
                    </Space>
                    <Space>
                        {isEnabled ? (
                            <Popconfirm
                                title="确认关闭组织级发送授权？"
                                description="关闭后，组织成员将无法使用此邮箱作为发件人。"
                                okText="确认关闭"
                                cancelText="取消"
                                okButtonProps={{ danger: true }}
                                onConfirm={handleDisable}
                                disabled={actionLoading}
                            >
                                <Button danger icon={<CloseCircleOutlined />} loading={actionLoading}>
                                    关闭授权
                                </Button>
                            </Popconfirm>
                        ) : (
                            <Button
                                type="primary"
                                icon={<ThunderboltOutlined />}
                                loading={actionLoading}
                                onClick={handleEnable}
                                disabled={!isSystemMailbox}
                            >
                                开启授权
                            </Button>
                        )}
                    </Space>
                </Space>
            </Card>
            {!isSystemMailbox && !isEnabled && (
                <Text type="warning" style={{ display: "block", marginTop: 8 }}>
                    <ExclamationCircleOutlined style={{ marginRight: 4 }} />
                    当前邮箱为个人邮箱，组织级授权不生效。如需授权，请使用邮箱级授权。
                </Text>
            )}
            {orgGrant && (
                <Text type="secondary" style={{ display: "block", marginTop: 8, fontSize: 12 }}>
                    授权人：{orgGrant.granted_by || "—"} · 创建时间：{dayjs(orgGrant.created_at).format("YYYY-MM-DD HH:mm:ss")}
                </Text>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// 主组件
// ---------------------------------------------------------------------------

const MailboxDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { message } = App.useApp();

    const [loading, setLoading] = useState(true);
    const [mailbox, setMailbox] = useState(null);
    const [platformDefaultMailbox, setPlatformDefaultMailbox] = useState(null);
    const [ingestSummary, setIngestSummary] = useState(null);
    const [pendingSummary, setPendingSummary] = useState(null);
    const [bjaAvailability, setBjaAvailability] = useState(null);
    const [pageError, setPageError] = useState(null);
    const [ingestSummaryError, setIngestSummaryError] = useState(null);
    const [pendingSummaryError, setPendingSummaryError] = useState(null);
    const [bjaAvailabilityError, setBjaAvailabilityError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [testResult, setTestResult] = useState(null);

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [mbRes, ingestRes, pendingRes, bjaAvailabilityRes, mailboxListRes] = await Promise.allSettled([
                getMailbox(id),
                getMailboxIngestSummary(id),
                getMailboxPendingSummary(id),
                getMailboxBusinessJudgmentAvailability(id),
                listMailboxes(),
            ]);

            if (mbRes.status === "rejected") throw mbRes.reason;
            const nextMailbox = validateMailboxDetailPayload(readMailHubData(mbRes.value, "邮箱详情"));

            let nextIngestSummary = null;
            let nextIngestSummaryError = null;
            if (ingestRes.status === "fulfilled") {
                try {
                    nextIngestSummary = validateMailboxIngestSummaryPayload(readMailHubData(ingestRes.value, "近期活动"));
                } catch (error) {
                    nextIngestSummaryError = formatMailHubError(error, "近期活动统计不可用");
                }
            } else {
                nextIngestSummaryError = formatMailHubError(ingestRes.reason, "近期活动统计不可用");
            }

            let nextPendingSummary = null;
            let nextPendingSummaryError = null;
            if (pendingRes.status === "fulfilled") {
                try {
                    nextPendingSummary = validateMailboxPendingSummaryPayload(readMailHubData(pendingRes.value, "处理积压摘要"));
                } catch (error) {
                    nextPendingSummaryError = formatMailHubError(error, "处理积压摘要不可用");
                }
            } else {
                nextPendingSummaryError = formatMailHubError(pendingRes.reason, "处理积压摘要不可用");
            }

            let nextBjaAvailability = null;
            let nextBjaAvailabilityError = null;
            if (bjaAvailabilityRes.status === "fulfilled") {
                try {
                    nextBjaAvailability = validateBjaAvailabilityPayload(readMailHubData(bjaAvailabilityRes.value, "BJA 可用性"));
                } catch (error) {
                    nextBjaAvailabilityError = formatMailHubError(error, "BJA 多层 Gate 状态不可用");
                }
            } else {
                nextBjaAvailabilityError = formatMailHubError(bjaAvailabilityRes.reason, "BJA 多层 Gate 状态不可用");
            }

            let nextPlatformDefaultMailbox = null;
            if (mailboxListRes.status === "fulfilled") {
                try {
                    const mailboxes = validateMailboxListPayload(readMailHubData(mailboxListRes.value, "收件箱列表"));
                    nextPlatformDefaultMailbox = mailboxes.find((item) => (
                        item.mailbox_type === "system_mailbox"
                        && item.is_platform_default
                        && item.id !== nextMailbox.id
                    )) || null;
                } catch {
                    nextPlatformDefaultMailbox = null;
                }
            }

            setMailbox(nextMailbox);
            setPlatformDefaultMailbox(nextPlatformDefaultMailbox);
            setIngestSummary(nextIngestSummary);
            setPendingSummary(nextPendingSummary);
            setBjaAvailability(nextBjaAvailability);
            setPageError(null);
            setIngestSummaryError(nextIngestSummaryError);
            setPendingSummaryError(nextPendingSummaryError);
            setBjaAvailabilityError(nextBjaAvailabilityError);
        } catch (error) {
            const errorMessage = formatMailHubError(error, "加载邮箱详情失败");
            setMailbox(null);
            setPlatformDefaultMailbox(null);
            setIngestSummary(null);
            setPendingSummary(null);
            setBjaAvailability(null);
            setPageError(errorMessage);
            setIngestSummaryError(null);
            setPendingSummaryError(null);
            setBjaAvailabilityError(null);
            message.error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [id, message]);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    // ---- 编辑保存 ----
    const handleSaveConfig = async (values) => {
        setSaving(true);
        try {
            await updateMailbox(id, values);
            message.success("配置已保存");
            await loadAll();
        } catch (err) {
            message.error(`保存配置失败: ${err?.response?.data?.message || err.message}`);
        } finally {
            setSaving(false);
        }
    };

    // ---- 启用/暂停轮询 ----
    const handleTogglePoll = async () => {
        if (!mailbox) return;
        const nextEnabled = !mailbox.is_poll_enabled;
        setActionLoading(true);
        try {
            await updateMailbox(id, { is_poll_enabled: nextEnabled });
            message.success(nextEnabled ? "已启用轮询" : "已暂停轮询");
            await loadAll();
        } catch {
            message.error("操作失败");
        } finally {
            setActionLoading(false);
        }
    };

    // ---- 手动同步 ----
    const handleTriggerSync = async () => {
        setActionLoading(true);
        try {
            const res = await triggerMailboxSync(id);
            const summary = validateIngestionSummaryPayload(readMailHubData(res, "手动同步结果"));
            message.success(
                `同步完成 — 抓取 ${summary.fetchedCount} 封`,
            );
            await loadAll();
        } catch (err) {
            message.error(`同步失败: ${err?.response?.data?.message || err.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    // ---- 测试连接 ----
    const handleTestConnection = async () => {
        setActionLoading(true);
        setTestResult(null);
        try {
            const res = await testMailboxConnection(id);
            const result = validateTestConnectionPayload(readMailHubData(res, "连接测试"));
            setTestResult(result);
            if (result?.success) {
                message.success(result.message);
            } else {
                message.warning(`连接失败: ${result.message}`);
            }
        } catch (err) {
            message.error(`测试连接失败: ${err?.response?.data?.message || err.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: "center", padding: 80 }}>
                <Spin size="large" tip="加载中…" />
            </div>
        );
    }

    if (pageError) {
        return <Result status="error" title="邮箱详情不可用" subTitle={pageError} extra={<Button onClick={() => navigate("/mail-hub/mailboxes")}>返回列表</Button>} />;
    }

    if (!mailbox) {
        return <Result status="404" title="邮箱不存在" extra={<Button onClick={() => navigate("/mail-hub/mailboxes")}>返回列表</Button>} />;
    }

    const currentMode = modeOption(mailbox.processing_mode);

    const tabItems = [
        {
            key: "connection",
            label: "连接配置",
            children: (
                <div>
                    <BasicConnectionSection mailbox={mailbox} platformDefaultMailbox={platformDefaultMailbox} onSave={handleSaveConfig} saving={saving} />
                    <Divider style={{ margin: "16px 0" }} />
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                        <Text strong>连接测试</Text>
                        <Button
                            icon={<LinkOutlined />}
                            loading={actionLoading}
                            onClick={handleTestConnection}
                        >
                            测试连接
                        </Button>
                    </div>
                    {testResult && (
                        <div>
                            <Tag
                                color={testResult.success ? "green" : "red"}
                                icon={testResult.success ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                            >
                                {testResult.message}
                            </Tag>
                            {Array.isArray(testResult.folders) && testResult.folders.length > 0 && (
                                <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                                    文件夹: {testResult.folders.join(", ")}
                                </Text>
                            )}
                        </div>
                    )}
                </div>
            ),
        },
        {
            key: "ingestion",
            label: "采集与处理",
            children: (
                <div>
                    <Card
                        title="采集配置与调度"
                        size="small"
                        style={{ marginBottom: 16 }}
                        extra={
                            <Space>
                                <Button
                                    type={mailbox.is_poll_enabled ? "default" : "primary"}
                                    icon={mailbox.is_poll_enabled ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                                    loading={actionLoading}
                                    onClick={handleTogglePoll}
                                >
                                    {mailbox.is_poll_enabled ? "暂停轮询" : "启用轮询"}
                                </Button>
                                <Button
                                    icon={<SyncOutlined />}
                                    loading={actionLoading}
                                    onClick={handleTriggerSync}
                                >
                                    手动同步
                                </Button>
                            </Space>
                        }
                    >
                        <SchedulingConfigSection mailbox={mailbox} onSave={handleSaveConfig} saving={saving} />
                    </Card>
                    <Card title="处理模式与业务副作用 Gate" size="small">
                        <ProcessingPolicySection mailbox={mailbox} bjaAvailability={bjaAvailability} bjaAvailabilityError={bjaAvailabilityError} onSave={handleSaveConfig} saving={saving} />
                    </Card>
                </div>
            ),
        },
        {
            key: "send-permission",
            label: "发送授权",
            children: (
                <SendAsPermissionSection mailboxId={id} mailboxType={mailbox.mailbox_type} />
            ),
        },
        {
            key: "runtime",
            label: "运行状态",
            children: (
                <div>
                    <Card title="近期活动 (24h)" size="small" style={{ marginBottom: 16 }}>
                        <RecentActivitySection mailbox={mailbox} ingestSummary={ingestSummary} ingestSummaryError={ingestSummaryError} />
                    </Card>
                    <Card title="处理积压摘要" size="small">
                        <PendingSummarySection pendingSummary={pendingSummary} pendingSummaryError={pendingSummaryError} />
                    </Card>
                </div>
            ),
        },
    ];

    return (
        <div>
            {/* 面包屑 + 标题 */}
            <Breadcrumb
                style={{ marginBottom: 12 }}
                items={[
                    { title: <Link to="/mail-hub/mailboxes">收件箱控制面</Link> },
                    { title: mailbox.email_address },
                ]}
            />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <Title level={4} style={{ margin: 0 }}>
                    <MailOutlined style={{ marginRight: 8 }} />
                    {mailbox.email_address}
                    {mailbox.display_name && (
                        <Text type="secondary" style={{ fontSize: 14, marginLeft: 12 }}>
                            ({mailbox.display_name})
                        </Text>
                    )}
                </Title>
                <Space>
                    <Button icon={<ReloadOutlined />} onClick={loadAll}>刷新</Button>
                    <Button onClick={() => navigate("/mail-hub/mailboxes")}>返回列表</Button>
                </Space>
            </div>

            {/* 状态摘要条 */}
            <Card size="small" style={{ marginBottom: 16 }}>
                <Space size="large" wrap>
                    <Space>
                        <Text type="secondary">收信</Text>
                        {mailbox.is_receive_enabled
                            ? <Tag color="green">收信已启用</Tag>
                            : <Tag color="default">收信未启用</Tag>}
                    </Space>
                    <Space>
                        <Text type="secondary">发信</Text>
                        {mailbox.is_send_enabled
                            ? <Tag color="green">发信已启用</Tag>
                            : <Tag color="default">发信未启用</Tag>}
                    </Space>
                    <Space>
                        <Text type="secondary">轮询</Text>
                        {mailbox.is_poll_enabled
                            ? <Tag color="blue">轮询中</Tag>
                            : <Tag color="default">已暂停</Tag>}
                    </Space>
                    <Space>
                        <Text type="secondary">处理模式</Text>
                        {currentMode
                            ? <Tag color={currentMode.color}>{currentMode.label}</Tag>
                            : <Tag color="error">未知</Tag>}
                    </Space>
                </Space>
            </Card>

            {/* Tab 布局 */}
            <Tabs defaultActiveKey="connection" items={tabItems} destroyInactiveTabPane={false} />
        </div>
    );
};

export default MailboxDetailPage;
